import { useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { type Case, Role } from "@/briefpoint-client";
import Loading from "@/components/Loading";
import SearchInput from "@/components/SearchInput/SearchInput";
import { useCaseApi, useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useFirm from "@/hooks/useFirm";
import "./styles.scss";
import { useMutation, useQuery } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useHistory } from "react-router-dom";
import AddEditCaseModal from "@/components/CaseManagement/AddEditCaseModal";
import CaseTableMulti from "@/components/CaseManagement/CaseTableMulti";
import PageHeader from "@/components/PageHeader";
import { toast } from "@/components/ui/toast";
import type { CaseResponse } from "@/lib/types";
import { normalizeError } from "@/lib/utils";

export default function Cases() {
  const { user, firm } = useAuth();
  if (!firm) throw new Error("Firm is undefined");
  const caseApi = useCaseApi();
  const tagApi = useTagApi();
  const history = useHistory();
  const [users, , , ,] = useFirm(firm?.id, false);
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState<Map<number, string> | undefined>();
  const { jurisdictions } = useAuth();
  const [showAddCase, setShowAddCase] = useState(false);

  const addCaseMutation = useMutation({
    mutationFn: async (_case: Case) => {
      return await caseApi.casePost({
        firmId: user?.firmId || "",
        _case,
      });
    },
    onSuccess: (data) => {
      toast.success("Case added successfully");
      history.push(`/cases/${data.id}`);
    },
    onError: (error) => {
      toast.error(error.message);
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => setShowAddCase(false),
  });

  const loadCasesQuery = useQuery({
    queryKey: [
      "caseApi.caseGetAll_1",
      {
        firmId: user?.firmId,
        usersCasesOnly: !user?.role?.includes(Role.FirmAdmin),
      },
    ],
    queryFn: async () => {
      if (user?.firmId) {
        const cases = (await caseApi.caseGetAll_1({
          firmId: user.firmId,
          usersCasesOnly: !user?.role?.includes(Role.FirmAdmin),
        })) as CaseResponse[];
        return cases;
      }
    },
  });

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();

      const loadedTags = await tagApi.tagFindTags({});

      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
      }
      setTags(tagsList);
    }
    if (!tags) {
      loadTags();
    }
  }, [tagApi, tags]);

  function getVenue(_case: CaseResponse) {
    const j = jurisdictions?.find((x) => x.id === _case.jurisdiction);
    return (
      j?.venues
        ?.find((x) => x.id === _case.venue)
        ?.shortName?.toLocaleLowerCase() ?? ""
    );
  }

  const filteredCases = loadCasesQuery.data?.filter((x) => {
    if (!search) return true;

    const title = x.title?.toLocaleLowerCase() ?? "";
    if (title.includes(search.toLowerCase())) {
      return true;
    }
    const shortTitle = x.shortTitle?.toLocaleLowerCase() ?? "";
    if (shortTitle.includes(search.toLowerCase())) {
      return true;
    }
    const caseNum = x.caseNumber?.toLocaleLowerCase() ?? "";
    if (caseNum.includes(search.toLowerCase())) {
      return true;
    }
    const venue = getVenue(x);
    if (venue.includes(search.toLowerCase())) {
      return true;
    }

    return false;
  });

  // Determines if we should show the "All Cases" list
  const shouldShowRecent = (filteredCases?.length ?? 0) > 5;

  return (
    <>
      <Container className="page-container-multi">
        <Loading
          isLoading={
            loadCasesQuery.isLoading ||
            jurisdictions === undefined ||
            users === undefined
          }
          className="mt-3"
        >
          <PageHeader
            testId="cases-header"
            title="Cases"
            cta={
              <Button
                data-testid="add-new-case-button"
                variant="primary"
                onClick={() => {
                  setShowAddCase(true);
                }}
              >
                Add New Case
              </Button>
            }
          />
          <div className="search-input-wrapper">
            <SearchInput
              value={search}
              placeholder="Search cases"
              onChange={(value) => setSearch(value.toLowerCase())}
              className="mb-2"
            />
          </div>
          {/* If there are no results, show a message */}
          {(filteredCases?.length ?? 0) <= 0 && !!search?.length ? (
            <div>No results found.</div>
          ) : (
            <div>
              {shouldShowRecent && (
                <>
                  <h2 style={{ margin: "0px", marginBottom: "8px" }}>
                    Recent Cases
                  </h2>

                  <CaseTableMulti
                    cases={filteredCases}
                    recent
                    addCase={() => setShowAddCase(true)}
                    users={users}
                    jurisdictions={jurisdictions}
                  />
                </>
              )}
            </div>
          )}
          <h2 style={{ marginTop: "15px", marginBottom: "8px" }}>All Cases</h2>
          <CaseTableMulti
            cases={filteredCases}
            addCase={() => setShowAddCase(true)}
            users={users}
            jurisdictions={jurisdictions}
          />
        </Loading>
      </Container>

      <AddEditCaseModal
        availableTags={tags}
        setAvailableTags={setTags}
        title="Add Case"
        confirm="Add"
        isShow={showAddCase}
        onClose={() => setShowAddCase(false)}
        onConfirm={addCaseMutation.mutate}
        users={users}
        jurisdictions={jurisdictions ?? []}
      />
    </>
  );
}
